import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import React, {useEffect, useRef, useState} from 'react';

import {useModal} from 'features/modal/hooks/useModal';
import {useAuth} from "features/authentication/hooks/useAuth";
import {LoginForm} from 'components/forms/LoginForm';
import {getSiteSpecificConfig} from 'features/services/utils/env-helper';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getLocatizedDayWithShortMonth} from 'features/services/utils/date-helpers';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {LinkStyle, HeadlineLevel} from 'features/ui/enums';
import {P} from 'components/atoms/Text/P';
import {ReadMore} from 'components/atoms/LinksAndButtons/ReadMore';
import {Button, ButtonColor, ButtonSize, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {Headline} from '@atoms/Text/Headline';

interface Props {
    small?: boolean;
    isStagePage?: boolean;
    startDate: Date;
    endDate: Date;
    objectId?: string;
    onStartPurchase?: () => void;
    onScrollClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    purchaseComplete?: boolean;
}

export const VipPeriodBanner: React.FunctionComponent<Props> = ({
    small, isStagePage, startDate, endDate, onScrollClick, onStartPurchase, objectId, purchaseComplete
}) => {
    const { open } = useModal();
    const {user, isLoggedIn} = useAuth();
    const [hasPurchase, setHasPurchase] = useState(false);
    const _loginLinkRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (user && user.purchases && user.purchases.length > 0 && objectId) {
            setHasPurchase(user.purchases.filter(purchase => purchase.objectId === objectId).length > 0);
            return;
        }
        setHasPurchase(false);
    }, [user]);

    useEffect(() => {
        if(purchaseComplete !== undefined && purchaseComplete)
            setHasPurchase(purchaseComplete);
    }, [purchaseComplete]);

    const onLogin = () => {
        open(
            <LoginForm />,
            { fullscreen: false, triggerElementId: small ? 'vipPeriodBanner-small' : 'vipPeriodBanner-large', ariaLabel: getFormattedMessage('jm.components.LoginForm.ariaLabel') }
        );
    };

    const getButtonText = () => {
        if(user?.isVip) {
            if(isStagePage) {
                return "jm.components.VipPeriodBanner.readMoreStage";
            } else if(hasPurchase) {
                return "jm.components.VipPeriodBanner.readMoreObjectPurchase";
            } else {
                return "jm.components.VipPeriodBanner.readMoreObject";
            }
        } else {
            if(isLoggedIn) {
                return "jm.components.VipPeriodBanner.readMoreUpgrade";
            } else {
                return "jm.components.VipPeriodBanner.readMoreONotLoggedIn";
            }
        }
    }

    const renderContent = () => {
        return (
            <>
                <Headline level={HeadlineLevel.H3}>
                    <FormattedMessage
                        id={user?.isVip
                            ? "jm.components.VipPeriodBanner.titleLoggedIn"
                            : "jm.components.VipPeriodBanner.titleNotLoggedIn"
                        }
                        values={{
                            startDate: getLocatizedDayWithShortMonth(startDate),
                            endDate: getLocatizedDayWithShortMonth(endDate),
                        }}
                    />
                </Headline>

                {!small && <P>
                    {isStagePage
                        ? user?.isVip
                            ? <FormattedMessage id="jm.components.VipPeriodBanner.descriptionStageLoggedIn" />
                            : <FormattedMessage id="jm.components.VipPeriodBanner.descriptionStageNotLoggedIn" />
                        : <FormattedMessage id="jm.components.VipPeriodBanner.descriptionObjectPage" />
                    }
                </P> }

                <div aria-hidden="true">
                    {small
                        ? <ReadMore
                            text={getFormattedMessage(getButtonText())}
                        />
                        : <Button
                            buttonColor={ButtonColor.LIGHT}
                            buttonSize={ButtonSize.DEFAULT}
                            buttonType={ButtonType.DEFAULT}
                        >
                            <FormattedMessage id={getButtonText()} />
                        </Button>
                    }
                </div>
            </>
        )
    };

    const getLink = () => {
        let href = '#';
        let onClick: ((e: React.MouseEvent<HTMLAnchorElement>) => void) | undefined;

        if (user?.isVip) {
            if (isStagePage) {
                onClick = onScrollClick;
            } else if (hasPurchase) {
                href = getSiteSpecificConfig().myResidences + '#purchases';
            } else {
                onClick = () => onStartPurchase && onStartPurchase();
            }
        } else {
            if (isLoggedIn) {
                href = getSiteSpecificConfig().createAccount;
            } else {
                onClick = onLogin;
            }
        }

        return (
            <A 
                className="banner-link" 
                href={href} 
                route
                variant={LinkStyle.UNSTYLED}
                onClick={onClick}
                title={getFormattedMessage(getButtonText())} 
            >
                <span className="visually-hidden">
                    <FormattedMessage id={getButtonText()} />
                </span>
            </A>
        );
    };

    const classes = classnames('vip-period-banner', {
        'vip-period-banner--small': small,
    });

    return (
        <div
            className={classes}
            id={small
                ? 'vipPeriodBanner-small'
                : 'vipPeriodBanner-large'}
        >
            {renderContent()}
            {getLink()}
        </div>
    );
};
